import React, { useEffect, useMemo, useState } from "react";
import "../StockInward.css";
import {
  addAmountReceivedEntry,
  deleteAmountReceivedEntry,
  getAmountReceivedEntries,
  updateAmountReceivedEntry,
} from "../storage";
import { formatDisplayDate, toComparableYmd } from "../utils/dateFormat";

const SECTION_OPTIONS = ["Delivery", "Stall", "Market"];
const PAYMENT_MODE_OPTIONS = ["Cash", "GPay", "Account", "Credit"];

function todayYmd() {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function makeDefaultForm() {
  return {
    date: todayYmd(),
    section: "Delivery",
    paymentMode: "Cash",
    amount: "",
    notes: "",
  };
}

function formatMoney(value) {
  return (Number(value) || 0).toFixed(2);
}

export default function AmountReceived({ currentUser }) {
  const [form, setForm] = useState(() => makeDefaultForm());
  const [editingId, setEditingId] = useState("");
  const [entries, setEntries] = useState([]);
  const [query, setQuery] = useState("");
  const [dateFromFilter, setDateFromFilter] = useState("");
  const [dateToFilter, setDateToFilter] = useState("");
  const [sectionFilter, setSectionFilter] = useState("All");
  const [modeFilter, setModeFilter] = useState("All");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState({});
  const isAdmin = String(currentUser?.role || "").toLowerCase() === "admin";

  const refresh = async () => {
    setLoading(true);
    try {
      const list = await getAmountReceivedEntries();
      setEntries(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error(err);
      alert(err?.message || "Failed to load amount received entries.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isAdmin) return;
    refresh();
  }, [isAdmin]);

  const resetForm = () => {
    setForm(makeDefaultForm());
    setEditingId("");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!isAdmin) return;

    const date = String(form.date || "").trim();
    const section = String(form.section || "").trim();
    const paymentMode = String(form.paymentMode || "").trim();
    const amount = Number(form.amount);
    const notes = String(form.notes || "").trim();

    if (!date || !section || !paymentMode) {
      alert("Date, sales section, and payment category are required.");
      return;
    }
    if (!amount || amount <= 0) {
      alert("Enter a valid amount.");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        date,
        section,
        paymentMode,
        amount,
        notes,
      };
      if (editingId) {
        await updateAmountReceivedEntry(editingId, payload);
      } else {
        await addAmountReceivedEntry(payload);
      }
      await refresh();
      resetForm();
    } catch (err) {
      console.error(err);
      alert(err?.message || "Failed to save amount received entry.");
    } finally {
      setSaving(false);
    }
  };

  const filteredEntries = useMemo(() => {
    const q = String(query || "").trim().toLowerCase();
    const fromDate = toComparableYmd(dateFromFilter);
    const toDate = toComparableYmd(dateToFilter);

    return (Array.isArray(entries) ? entries : []).filter((entry) => {
      const entryDate = toComparableYmd(entry?.date);
      const entrySection = String(entry?.section || "").trim();
      const entryMode = String(entry?.paymentMode || "").trim();

      if (fromDate && (!entryDate || entryDate < fromDate)) return false;
      if (toDate && (!entryDate || entryDate > toDate)) return false;
      if (sectionFilter !== "All" && entrySection !== sectionFilter) return false;
      if (modeFilter !== "All" && entryMode !== modeFilter) return false;
      if (!q) return true;

      return (
        entrySection.toLowerCase().includes(q) ||
        entryMode.toLowerCase().includes(q) ||
        String(entry?.notes || "").toLowerCase().includes(q) ||
        String(entry?.createdByName || "").toLowerCase().includes(q)
      );
    });
  }, [dateFromFilter, dateToFilter, entries, modeFilter, query, sectionFilter]);

  const totals = useMemo(() => {
    const totalAmount = filteredEntries.reduce((sum, entry) => sum + (Number(entry?.amount) || 0), 0);
    const dateSet = new Set(filteredEntries.map((entry) => String(entry?.date || "").trim()).filter(Boolean));
    return {
      count: filteredEntries.length,
      dateCount: dateSet.size,
      totalAmount: +totalAmount.toFixed(2),
    };
  }, [filteredEntries]);

  const dateWiseTotals = useMemo(() => {
    const map = new Map();
    for (const entry of filteredEntries) {
      const date = String(entry?.date || "").trim();
      if (!date) continue;
      if (!map.has(date)) {
        map.set(date, { date, amount: 0, count: 0 });
      }
      const row = map.get(date);
      row.amount += Number(entry?.amount) || 0;
      row.count += 1;
    }
    return Array.from(map.values())
      .map((row) => ({ ...row, amount: +Number(row.amount || 0).toFixed(2) }))
      .sort((a, b) => String(b.date || "").localeCompare(String(a.date || "")));
  }, [filteredEntries]);

  const sectionSummary = useMemo(() => {
    return SECTION_OPTIONS.map((section) => {
      const modeTotals = PAYMENT_MODE_OPTIONS.reduce((acc, mode) => {
        acc[mode] = 0;
        return acc;
      }, {});
      let total = 0;
      for (const entry of filteredEntries) {
        if (String(entry?.section || "").trim() !== section) continue;
        const mode = String(entry?.paymentMode || "").trim();
        const amount = Number(entry?.amount) || 0;
        if (Object.prototype.hasOwnProperty.call(modeTotals, mode)) {
          modeTotals[mode] += amount;
        }
        total += amount;
      }
      return {
        section,
        modeTotals: Object.fromEntries(
          Object.entries(modeTotals).map(([mode, amount]) => [mode, +Number(amount || 0).toFixed(2)])
        ),
        total: +total.toFixed(2),
      };
    });
  }, [filteredEntries]);

  const startEdit = (entry) => {
    if (!entry?.id) return;
    setEditingId(entry.id);
    setForm({
      date: String(entry?.date || "").slice(0, 10) || todayYmd(),
      section: String(entry?.section || "Delivery"),
      paymentMode: String(entry?.paymentMode || "Cash"),
      amount: Number(entry?.amount) || "",
      notes: String(entry?.notes || ""),
    });
    try {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (error) {
      // ignore scroll errors
    }
  };

  const handleDelete = async (entry) => {
    if (!entry?.id) return;
    if (!window.confirm("Delete this amount received entry?")) return;
    setDeleting((prev) => ({ ...prev, [entry.id]: true }));
    try {
      await deleteAmountReceivedEntry(entry.id);
      if (editingId === entry.id) {
        resetForm();
      }
      await refresh();
    } catch (err) {
      console.error(err);
      alert(err?.message || "Failed to delete amount received entry.");
    } finally {
      setDeleting((prev) => {
        const next = { ...prev };
        delete next[entry.id];
        return next;
      });
    }
  };

  if (!isAdmin) {
    return (
      <div className="stock-inward-container" style={{ paddingTop: 0 }}>
        <div className="form-card">
          <div className="card-header">
            <h2 className="card-title">Daily Amount Received</h2>
          </div>
          <div className="inward-form">
            <div className="empty-state" style={{ padding: "16px" }}>
              You do not have access to this page.
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="stock-inward-container" style={{ paddingTop: 0 }}>
      <div className="form-card">
        <div className="card-header">
          <h2 className="card-title">Daily Amount Received</h2>
          <span className="entry-count">{editingId ? "Edit Mode" : "Admin Entry"}</span>
        </div>
        <form className="inward-form" onSubmit={handleSubmit}>
          <div className="form-grid">
            <div className="field fl-field">
              <input
                id="amount-received-date"
                className="fl-input input-field"
                type="date"
                value={form.date}
                onChange={(event) => setForm((prev) => ({ ...prev, date: event.target.value }))}
                placeholder=" "
                required
              />
              <label htmlFor="amount-received-date" className="fl-label">
                Receipt Date
              </label>
            </div>

            <div className="field fl-field">
              <select
                id="amount-received-section"
                className="fl-input input-field"
                value={form.section}
                onChange={(event) => setForm((prev) => ({ ...prev, section: event.target.value }))}
                style={{ paddingTop: "14px" }}
                required
              >
                {SECTION_OPTIONS.map((section) => (
                  <option key={section} value={section}>
                    {section}
                  </option>
                ))}
              </select>
              <label htmlFor="amount-received-section" className="fl-label">
                Sales Section
              </label>
            </div>

            <div className="field fl-field">
              <select
                id="amount-received-mode"
                className="fl-input input-field"
                value={form.paymentMode}
                onChange={(event) => setForm((prev) => ({ ...prev, paymentMode: event.target.value }))}
                style={{ paddingTop: "14px" }}
                required
              >
                {PAYMENT_MODE_OPTIONS.map((mode) => (
                  <option key={mode} value={mode}>
                    {mode}
                  </option>
                ))}
              </select>
              <label htmlFor="amount-received-mode" className="fl-label">
                Payment Category
              </label>
            </div>

            <div className="field fl-field">
              <input
                id="amount-received-value"
                className="fl-input input-field"
                type="number"
                step="0.01"
                inputMode="decimal"
                value={form.amount}
                onChange={(event) => setForm((prev) => ({ ...prev, amount: event.target.value }))}
                placeholder=" "
                required
              />
              <label htmlFor="amount-received-value" className="fl-label">
                Amount
              </label>
            </div>

            <div className="field full fl-field">
              <textarea
                id="amount-received-notes"
                className="fl-input input-field"
                rows={2}
                value={form.notes}
                onChange={(event) => setForm((prev) => ({ ...prev, notes: event.target.value }))}
                placeholder=" "
              />
              <label htmlFor="amount-received-notes" className="fl-label">
                Notes (Optional)
              </label>
            </div>
          </div>

          <div className="form-actions">
            {editingId ? (
              <>
                <button type="button" className="btn-secondary" onClick={resetForm} disabled={saving}>
                  Cancel Edit
                </button>
                <button type="submit" className="btn-primary" disabled={saving}>
                  {saving ? "Updating..." : "Update Entry"}
                </button>
              </>
            ) : (
              <button type="submit" className="btn-primary" disabled={saving}>
                {saving ? "Saving..." : "Save Entry"}
              </button>
            )}
          </div>
        </form>
      </div>

      <div className="entries-card">
        <div className="card-header" style={{ borderBottom: "none", gap: 12, flexWrap: "wrap" }}>
          <h2 className="card-title">Saved Amount Received</h2>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
              <label style={{ fontSize: 12, color: "#64748b", fontWeight: 700 }}>From</label>
              <input
                type="date"
                className="input-field"
                value={dateFromFilter}
                onChange={(event) => setDateFromFilter(event.target.value)}
                style={{ width: 160 }}
              />
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
              <label style={{ fontSize: 12, color: "#64748b", fontWeight: 700 }}>To</label>
              <input
                type="date"
                className="input-field"
                value={dateToFilter}
                onChange={(event) => setDateToFilter(event.target.value)}
                style={{ width: 160 }}
              />
            </div>
            <select
              className="input-field"
              value={sectionFilter}
              onChange={(event) => setSectionFilter(event.target.value)}
              style={{ width: 170 }}
            >
              <option value="All">All Sections</option>
              {SECTION_OPTIONS.map((section) => (
                <option key={section} value={section}>
                  {section}
                </option>
              ))}
            </select>
            <select
              className="input-field"
              value={modeFilter}
              onChange={(event) => setModeFilter(event.target.value)}
              style={{ width: 170 }}
            >
              <option value="All">All Categories</option>
              {PAYMENT_MODE_OPTIONS.map((mode) => (
                <option key={mode} value={mode}>
                  {mode}
                </option>
              ))}
            </select>
            <input
              type="search"
              className="input-field"
              placeholder="Search notes / user / section..."
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              style={{ width: 240 }}
            />
            <button
              type="button"
              className="btn-secondary"
              onClick={() => {
                setDateFromFilter("");
                setDateToFilter("");
                setSectionFilter("All");
                setModeFilter("All");
                setQuery("");
              }}
            >
              Clear Filters
            </button>
            <button type="button" className="btn-secondary" onClick={refresh} disabled={loading}>
              {loading ? "Refreshing..." : "Refresh"}
            </button>
          </div>
        </div>

        <div style={{ padding: "0 16px 12px" }}>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <div
              style={{
                padding: "8px 12px",
                borderRadius: 10,
                border: "1px solid #cbd5e1",
                background: "#f8fafc",
                minWidth: 140,
              }}
            >
              <div style={{ fontSize: 12, color: "#64748b" }}>Entries</div>
              <div style={{ fontWeight: 800 }}>{totals.count}</div>
            </div>
            <div
              style={{
                padding: "8px 12px",
                borderRadius: 10,
                border: "1px solid #cbd5e1",
                background: "#f8fafc",
                minWidth: 140,
              }}
            >
              <div style={{ fontSize: 12, color: "#64748b" }}>Dates</div>
              <div style={{ fontWeight: 800 }}>{totals.dateCount}</div>
            </div>
            <div
              style={{
                padding: "8px 12px",
                borderRadius: 10,
                border: "1px solid #bfdbfe",
                background: "#eff6ff",
                minWidth: 180,
              }}
            >
              <div style={{ fontSize: 12, color: "#1d4ed8" }}>Total Amount</div>
              <div style={{ fontWeight: 800, color: "#1d4ed8" }}>{formatMoney(totals.totalAmount)}</div>
            </div>
          </div>
        </div>

        <div style={{ padding: "0 16px 12px" }}>
          <div style={{ fontWeight: 700, marginBottom: 8, color: "#334155" }}>Section-wise Received Summary</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 10 }}>
            {sectionSummary.map((section) => (
              <div
                key={section.section}
                style={{
                  border: "1px solid #dbeafe",
                  background: "#f8fbff",
                  borderRadius: 14,
                  padding: "12px 14px",
                }}
              >
                <div style={{ fontWeight: 900, color: "#1e3a8a", marginBottom: 10 }}>{section.section}</div>
                <div style={{ display: "grid", gap: 6 }}>
                  {PAYMENT_MODE_OPTIONS.map((mode) => (
                    <div key={`${section.section}-${mode}`} style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                      <span style={{ color: "#475569", fontWeight: 700 }}>{mode}</span>
                      <strong style={{ color: "#0f172a" }}>{formatMoney(section.modeTotals[mode])}</strong>
                    </div>
                  ))}
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 12,
                      paddingTop: 8,
                      marginTop: 4,
                      borderTop: "1px dashed #bfdbfe",
                    }}
                  >
                    <span style={{ color: "#1e3a8a", fontWeight: 900 }}>Section Total</span>
                    <strong style={{ color: "#1e3a8a" }}>{formatMoney(section.total)}</strong>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {dateWiseTotals.length > 0 ? (
          <div style={{ padding: "0 16px 8px" }}>
            <div style={{ fontWeight: 700, marginBottom: 8, color: "#334155" }}>Date-wise Totals</div>
            <div className="entries-table-container" style={{ margin: 0, padding: 12 }}>
              <table className="entries-table" style={{ minWidth: 480 }}>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th className="text-right">Entries</th>
                    <th className="text-right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {dateWiseTotals.slice(0, 10).map((row) => (
                    <tr key={row.date}>
                      <td>{formatDisplayDate(row.date)}</td>
                      <td className="text-right">{row.count}</td>
                      <td className="text-right">{formatMoney(row.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}

        <div className="entries-table-container">
          <table className="entries-table" style={{ minWidth: 980 }}>
            <thead>
              <tr>
                <th style={{ width: 130 }}>Date</th>
                <th style={{ width: 150 }}>Section</th>
                <th style={{ width: 150 }}>Category</th>
                <th>Notes</th>
                <th style={{ width: 170 }}>Entered By</th>
                <th className="text-right" style={{ width: 140 }}>Amount</th>
                <th style={{ width: 170 }} className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredEntries.length === 0 ? (
                <tr>
                  <td colSpan={7} className="empty-state" style={{ padding: 16 }}>
                    No amount received entries found
                  </td>
                </tr>
              ) : (
                filteredEntries.map((entry) => (
                  <tr key={entry.id}>
                    <td>{formatDisplayDate(entry.date)}</td>
                    <td>{entry.section}</td>
                    <td>{entry.paymentMode}</td>
                    <td>{entry.notes || "-"}</td>
                    <td>{entry.createdByName || "-"}</td>
                    <td className="text-right" style={{ fontWeight: 700 }}>
                      {formatMoney(entry.amount)}
                    </td>
                    <td className="text-right">
                      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                        <button
                          type="button"
                          className="btn-secondary"
                          onClick={() => startEdit(entry)}
                          style={{ padding: "0.35rem 0.7rem", fontSize: "0.75rem" }}
                          disabled={!!deleting[entry.id]}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="btn-remove"
                          onClick={() => handleDelete(entry)}
                          style={{ width: 32, height: 32, padding: 0 }}
                          disabled={!!deleting[entry.id]}
                        >
                          x
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
